import { ELEMENTS, type Locale } from '../constants'
import type { NameReading } from './nameReading'
import { nameSound, soundTraits, type Coda, type NameSound, type Onset, type Vowel } from './nameSound'
import { elementBalance } from '../elementBalance'

/**
 * 이름 풀이 본문을 AI 에게 맡길 때 **넘기는 재료**.
 *
 * 계산은 전부 여기(화면)에서 끝낸다. 서버는 이 값으로 프롬프트만 만든다 —
 * 그래서 AI 가 기운 이름·숫자를 **그 언어로** 그대로 쓸 수 있다
 * (`/api/quick` 이 `dayMaster` 를 받는 것과 같은 모양이다).
 *
 * 이 모듈은 **프롬프트 모듈을 import 하지 않는다.** 그쪽은 서버 전용이라
 * 클라이언트 번들에 들어가면 안 된다 — 값의 모양만 맞춘다.
 */

export interface NameMaterial {
  locale: Locale
  name: string
  /**
   * 글자마다 소리·기운·자리에 **소리 결**까지.
   * 오행만 보면 한국 이름은 흙·쇠에 몰려 같은 조합이 되어, 어떤 이름을 넣어도 같은 글이
   * 나왔다 (제보). 받침·모음·첫소리 세기는 오행이 같아도 갈린다.
   */
  letters: {
    text: string
    element: string
    where: 'first' | 'middle' | 'last' | 'only'
    coda: Coda
    vowel: Vowel
    onset: Onset
    /** 한자의 실제 읽기 (ko 한글 음절 · zh 병음 · ja 음독) — 소리 설명이 이 읽기를 따른다 */
    reading?: string
    /** 한자인데 읽기를 모른다 — 소리 결을 적지 않는다 */
    readingUnknown?: boolean
  }[]
  /** 이름 전체의 소리 결 */
  sound: NameSound
  /** 권하는 첫소리 — 그 언어의 자모·알파벳 (별명 단락에서 규칙으로 먼저 말한다) */
  suggestSounds: string
  pairs: { from: string; to: string; bond: NameReading['pairs'][number]['bond'] }[]
  needed: string
  strongest: string
  /** 사주의 가장 많은·적은 기운 모두 (같은 수면 여럿 — 그 언어 이름). counts 는 이름이 담은 기운만이라 여기서 따로 */
  chartMost: string[]
  chartLeast: string[]
  /** 사주 다섯 기운 수 **전부 (0개 포함)** — counts 는 이름이 담은 기운만이라 0개인 기운이 재료에 없었다 (Beto "물이 조금") */
  chartAll: { element: string; count: number }[]
  counts: { element: string; chart: number; name: number; total: number }[]
  /** 'over' : 채운 기운이 넘쳐 가장 많아졌다 — 화면의 nameFit_over 와 같은 판정 */
  fit: NameReading['fit'] | 'over'
  fitLetters: string[]
  coverLetters: { text: string; element: string }[]
  pileLetters: { text: string; element: string }[]
  suggest: string
  nickPicks: string[]
}

/** AI 가 쓴 세 토막 */
export interface NameParts {
  letters: string
  chart: string
  /** 내 사주와 나란히 (새 글부터) */
  side?: string
  /** 별명·영어 이름 3~5개 — 예전에 다시 AI 가 쓴다 (v2 까지의 옛 글에도 있다) */
  nick?: string
}

export function isNameParts(v: unknown): v is NameParts {
  const p = v as Partial<NameParts> | null
  return (
    !!p &&
    typeof p.letters === 'string' &&
    typeof p.chart === 'string' &&
    p.letters !== '' &&
    p.chart !== ''
  )
}

/**
 * 재료를 만든다.
 * @param elementName 오행 이름을 그 언어로 옮기는 함수 (화면의 `t`)
 * @param nickPicks 화면이 이미 고른 예시 이름 셋 — AI 가 바꾸지 못한다
 */
export function japaneseNameReadings(name: string, readings: Record<string, string>): Record<string, string> {
  const result = { ...readings }
  if (name.includes('一郎')) result['一'] = 'いち'
  if (name.includes('次郎')) result['次'] = 'じ'
  if (name.includes('三郎')) result['三'] = 'さぶ'
  if (name.includes('王芳')) { result['王'] = 'おう'; result['芳'] = 'ほう' }
  return result
}

export function nameMaterial(
  locale: Locale,
  name: string,
  reading: NameReading,
  elementName: (e: string) => string,
  nickPicks: string[],
  /** 권하는 첫소리 (그 언어의 자모·알파벳). 별명 단락에서 규칙으로 먼저 말한다. */
  suggestSounds = '',
  /** 서버가 읽어 준 한자 음 (글자 → 읽기) */
  readings: Record<string, string> = {},
): NameMaterial {
  const nameReadings = locale === 'ja' ? japaneseNameReadings(name, readings) : readings
  const readable = reading.units.filter((u) => u.element !== null)
  const letters: NameMaterial['letters'] = readable.map((u, i) => {
    /*
     * 소리 결은 **실제 읽기**로 잰다. 예전에는 한자 글자 그대로 재어 늘 "받침 없음" 이 되었고,
     * AI 가 鄭在桓 을 "모두 받침 없이"(정 ㅇ · 환 ㄴ), 張 을 "沒有收尾音"(zhāng 은 ng) 이라 썼다.
     */
    const said = nameReadings[u.text]
    const t = soundTraits(said ?? u.text)
    // 한자인데 읽기가 없으면 '받침 없음' 이 아니라 '모름'
    const unknown = !said && /\p{Script=Han}/u.test(u.text)
    return {
      ...(said && said !== u.text ? { reading: said } : {}),
      ...(unknown ? { readingUnknown: true } : {}),
      text: u.text,
      element: elementName(u.element!),
      where:
        readable.length === 1 ? 'only'
        : i === 0 ? 'first'
        : i === readable.length - 1 ? 'last'
        : 'middle',
      coda: t?.coda ?? 'none',
      vowel: t?.vowel ?? 'neutral',
      onset: t?.onset ?? 'plain',
    }
  })
  return {
    locale,
    name,
    letters,
    sound: nameSound(readable.map((u) => nameReadings[u.text] ?? u.text)),
    suggestSounds,
    pairs: reading.pairs.map((p) => ({ from: p.from, to: p.to, bond: p.bond })),
    needed: elementName(reading.needed),
    strongest: elementName(reading.strongest),
    chartMost: elementBalance(reading.chartCounts).most.map(elementName),
    chartLeast: elementBalance(reading.chartCounts).least.map(elementName),
    chartAll: ELEMENTS.map((e) => ({ element: elementName(e), count: reading.chartCounts[e] })),
    // **이름이 담은 기운만** 넘긴다 — 0 + 0 = 0 짜리 줄은 할 말이 없다
    counts: reading.nameElements.map((e) => ({
      element: elementName(e),
      chart: reading.chartCounts[e],
      name: reading.counts[e],
      total: reading.combined[e],
    })),
    fit: reading.pileList.length > 0 && reading.pileList.every((e) => reading.coverList.includes(e)) ? 'over' : reading.fit,
    fitLetters: reading.fitLetters,
    coverLetters: reading.coverLetters.map((l) => ({ text: l.text, element: elementName(l.element) })),
    // 채우는 기운은 "보태는" 쪽에 다시 넣지 않는다 — 화면의 "더 세지는 기운" 칸(채움 뺀 값)과 같게
    pileLetters: reading.pileLetters.filter((l) => !reading.coverList.includes(l.element)).map((l) => ({ text: l.text, element: elementName(l.element) })),
    suggest: elementName(reading.suggest),
    nickPicks,
  }
}
